import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import Spinner from "../components/Spinner";
import {
  PRODUCT_CONDITIONS,
  ALLOWED_CURRENCIES,
  DIMENSION_UNIT,
  WEIGHT_UNIT,
} from "../constants/productAddingConstants";

const API_BASE = process.env.REACT_APP_PRODUCT_SERVICE_URL;
const DRAFT_KEY = "add_product_draft";

/* =========================
   Small Reusable UI Helpers
========================= */
const Required = () => <span className="text-danger">*</span>;

const emptyForm = {
  categoryId: null,
  subCategoryId: null,

  productName: "",
  description: "",
  brand: "",
  modelNumber: "",

  costPrice: null,
  taxRate: null,

  currency: "ETB",
  condition: "NEW", // default

  dimensionUnit: "cm",
  weightUnit: "kg",

  returnPolicy: "",
  warrantyPeriod: "",
};
const createEmptyVariant = () => ({
  sku: "",
  price: "",
  availableQuantity: "",
  weight: "",
  attributes: {},
  images: [],
  previews: [],
});

export default function AddProduct() {
  const [attributesMeta, setAttributesMeta] = useState({
    productAttributes: [],
    variantAttributes: [],
  });

  const [productAttributesValues, setProductAttributesValues] = useState({});

  const [variants, setVariants] = useState([createEmptyVariant()]);

  const { auth } = useAuth();
  const sellerId = auth?.userId;

  const [categories, setCategories] = useState([]);

  const [subCategories, setSubCategories] = useState([]);

  const [form, setForm] = useState(emptyForm);
  const [touched, setTouched] = useState({});

  const [step, setStep] = useState(1);

  const [productImages, setProductImages] = useState([]);
  const [productPreviews, setProductPreviews] = useState([]);

  const [imageError, setImageError] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });

  const step3Fields = [
    {
      name: "productName",
      label: "Product Name",
      type: "text",
      required: true,
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: true,
    },
    {
      name: "brand",
      label: "Brand",
      type: "text",
      required: false,
    },
    {
      name: "modelNumber",
      label: "Model Number",
      type: "text",
      required: false,
    },
    {
      name: "condition",
      label: "Condition",
      type: "select",
      required: true,
      options: PRODUCT_CONDITIONS,
    },
    {
      name: "currency",
      label: "Currency",
      type: "select",
      required: true,
      options: ALLOWED_CURRENCIES,
    },

    {
      name: "dimensionUnit",
      label: "Dimension Unit",
      type: "select",
      required: true,
      options: DIMENSION_UNIT,
    },
    {
      name: "weightUnit",
      label: "Weight Unit",
      type: "select",
      required: true,
      options: WEIGHT_UNIT,
    },
    {
      name: "returnPolicy",
      label: "Return Policy",
      required: false,
      type: "text",
    },
    {
      name: "warrantyPeriod",
      label: "Warranty Period in months",
      required: false,
      type: "number",
    },
  ];

  /* =========================
     Fetch Categories
  ========================== */
  useEffect(() => {
    if (!API_BASE) return;
    fetch(`${API_BASE}/products/categories?page=0&size=100`)
      .then((res) => res.json())
      .then((res) => setCategories(res.content || []))
      .catch(() =>
        setStatus({ type: "error", message: "Failed to load categories." }),
      );
  }, []);

  /* =========================
     Fetch Subcategories
  ========================== */
  const handleCategoryChange = (e) => {
    const selectedId = Number(e.target.value);

    setForm((prev) => ({
      ...prev,
      categoryId: selectedId,
      subCategoryId: null, // reset subcategory
    }));

    fetch(`${API_BASE}/products/subcategories/${selectedId}`)
      .then((res) => res.json())
      .then((res) => {
        if (Array.isArray(res)) {
          setSubCategories(res);
        } else if (Array.isArray(res.content)) {
          setSubCategories(res.content);
        } else {
          setSubCategories([]);
        }
      })
      .catch(() =>
        setStatus({ type: "error", message: "Failed to load subcategories." }),
      );
  };

  const handleSubcategoryChange = (e) => {
    const selectedSubId = Number(e.target.value);

    setForm((prev) => ({
      ...prev,
      subCategoryId: selectedSubId,
    }));
  };

  // Fetching product attributes based on subcategory
  useEffect(() => {
    if (!form.subCategoryId) return;

    fetch(`${API_BASE}/products/subcategories/${form.subCategoryId}/attributes`)
      .then((res) => res.json())
      .then((data) => {
        setAttributesMeta(data);
      })

      .catch(() =>
        setStatus({ type: "error", message: "Failed to load attributes." }),
      );
  }, [form.subCategoryId]);

  /* =========================
     Draft Restore
  ========================== */
  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (draft) {
      const parsed = JSON.parse(draft);
      setForm(parsed.form || emptyForm);
      setStep(parsed.step || 1);
    }
  }, []);

  /* =========================
     Draft Auto Save
  ========================== */
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, step }));
  }, [form, step]);

  /* =========================
     Image Preview + validation
  ========================== */

  useEffect(() => {
    if (!productImages.length) {
      setProductPreviews([]);
      return;
    }

    const urls = productImages.map((file) => URL.createObjectURL(file));
    setProductPreviews(urls);
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [productImages]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const maxSize = 1 * 1024 * 1024;

    const validFiles = [];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        setImageError("Only JPEG, JPG, PNG, or WEBP images are allowed.");
        return;
      }
      if (file.size > maxSize) {
        setImageError("Each image must be under 1MB.");
        return;
      }
      validFiles.push(file);
    }

    setImageError("");

    setProductImages((prev) => [...prev, ...validFiles]);
  };

  /* =========================
     Handlers
  ========================== */

  const handleChange = (e) => {
    // const { name, value } = e.target;
    const { name, type, value, checked } = e.target;

    const numericFields = [
      "categoryId",
      "subCategoryId",
      "price",
      "availableQuantity",
      "weight",
      "width",
      "height",
      "length",
    ];

    let finalValue = value;
    if (type === "checkbox") {
      finalValue = checked;
    } else if (numericFields.includes(name)) {
      // Allow empty field while typing
      if (value === "") {
        finalValue = "";
      } else {
        const num = Number(value);

        // Prevent NaN and negative values
        if (isNaN(num) || num < 0) return;

        finalValue = num;
      }
    } else {
      finalValue = value;
    }

    setForm((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  /* =========================
     Validation
  ========================== */
  const requiredFields = {
    1: ["categoryId"],
    2: ["subCategoryId"],
    3: step3Fields.filter((f) => f.required).map((f) => f.name),
  };

  const isStepValid = (s) => {
    const fields =
      s === 3
        ? step3Fields.filter((f) => f.required).map((f) => f.name)
        : requiredFields[s] || [];

    const requiredValid = fields.every((f) => {
      const value = form[f];
      return (
        value !== undefined && value !== null && String(value).trim() !== ""
      );
    });

    if (!requiredValid) return false;

    if (s === 3) {
      const hasFilledVariant = variants.some(
        (v) => v.sku || v.price || v.availableQuantity,
      );
      if (!hasFilledVariant) return false;

      const variantsValid = variants.every((v) => {
        const hasSku = v.sku && v.sku.trim() !== "";

        const priceValid =
          v.price !== "" && !isNaN(Number(v.price)) && Number(v.price) >= 0;

        const qtyValid =
          v.availableQuantity !== "" &&
          !isNaN(Number(v.availableQuantity)) &&
          Number(v.availableQuantity) >= 0;

        return hasSku && priceValid && qtyValid;
      });

      if (!variantsValid) return false;
    }

    return true;
  };

  const markStepTouched = (s) => {
    setTouched((prev) => {
      const next = { ...prev };

      (requiredFields[s] || []).forEach((f) => {
        next[f] = true;
      });

      // ALSO mark step3Fields dynamically
      if (s === 3) {
        step3Fields.forEach((f) => {
          next[f.name] = true;
        });
      }

      return next;
    });
  };

  const StepIndicator = () => (
    <div className="mb-4">
      <div className="d-flex justify-content-between">
        {["Category", "Subcategory", "Details"].map((label, index) => {
          const s = index + 1;
          return (
            <div
              key={s}
              className={`text-center flex-fill ${
                step === s
                  ? "fw-bold text-primary"
                  : step > s
                    ? "text-success"
                    : "text-muted"
              }`}
            >
              <div
                className={`rounded-circle mx-auto mb-1 ${
                  step >= s ? "bg-primary text-white" : "bg-light"
                }`}
                style={{
                  width: 32,
                  height: 32,
                  lineHeight: "32px",
                }}
              >
                {s}
              </div>
              <small>{label}</small>
            </div>
          );
        })}
      </div>
      <hr />
    </div>
  );
  /* =========================
     Reset After Success
  ========================== */
  const resetAll = () => {
    localStorage.removeItem(DRAFT_KEY);
    setStep(1);
    setForm(emptyForm);
    setTouched({});
    setProductImages([]);
    setProductPreviews([]);
  };

  const uploadImages = async (files, ownerId) => {
    if (!files || files.length === 0) return [];

    const formData = new FormData();
    files.forEach((file) => formData.append("imageFiles", file));
    formData.append("ownerId", ownerId);

    const res = await fetch(`${API_BASE}/products/upload/images`, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) throw new Error("Image upload failed");

    return await res.json(); // [{id, url}]
  };

  /* =========================
     Submit
  ========================== */
  const handleSubmit = async () => {
    if (!isStepValid(3)) {
      markStepTouched(3);
      setStatus({
        type: "error",
        message: "Please complete all required fields.",
      });
      return;
    }

    setSubmitting(true);
    setStatus({ type: null, message: "" });

    try {
      // =========================
      // 1. Upload PRODUCT images
      // =========================
      const productImagesMeta = await uploadImages(productImages, sellerId);

      // =========================
      // 2. Upload VARIANT images
      // =========================
      const updatedVariants = await Promise.all(
        variants.map(async (v) => {
          const uploaded = await uploadImages(v.images, sellerId);

          return {
            ...v,
            imageIds: uploaded.map((img) => img.id),
          };
        }),
      );

      // =========================
      // 3. Build payload
      // =========================
      const payload = buildPayload({
        form,
        sellerId,
        productImagesMeta,
        variants: updatedVariants,
        attributesMeta,
        productAttributesValues,
      });

      // =========================
      // 4. Create Product
      // =========================
      const res = await fetch(`${API_BASE}/products/product/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Product creation failed");

      setStatus({
        type: "success",
        message: "Product created successfully.",
      });

      setTimeout(() => {
        resetAll();
        setStatus({ type: null, message: "" });
      }, 1500);
    } catch (err) {
      console.error(err);

      setStatus({
        type: "error",
        message: err.message || "Something went wrong",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const buildPayload = ({
    form,
    sellerId,
    productImagesMeta,
    variants,
    attributesMeta,
    productAttributesValues,
  }) => {
    const hasVariants = variants.length > 0;

    const mapAttributes = (attrs, attributesMeta) => {
      return Object.entries(attrs || {}).map(([code, value]) => {
        const allAttributes = [
          ...attributesMeta.productAttributes,
          ...attributesMeta.variantAttributes,
        ];

        const meta = allAttributes.find((a) => a.code === code);

        if (!meta) {
          throw new Error(`Attribute metadata not found for ${code}`);
        }

        //  CASE 1: Attribute has predefined options (ENUM / SELECT)
        if (meta.options && meta.options.length > 0) {
          const option = meta.options.find((opt) => opt.id === Number(value));

          if (!option) {
            throw new Error(`Invalid value "${value}" for ${code}`);
          }

          return {
            attributeCode: code,
            optionId: option.id,
            valueText: null,
            valueNumber: null,
            valueBoolean: null,
          };
        }

        //  CASE 2: NUMBER (no options)
        if (meta.type === "NUMBER") {
          return {
            attributeCode: code,
            optionId: null,
            valueText: null,
            valueNumber: value !== "" ? Number(value) : null,
            valueBoolean: null,
          };
        }

        //  CASE 3: BOOLEAN
        if (meta.type === "BOOLEAN") {
          return {
            attributeCode: code,
            optionId: null,
            valueText: null,
            valueNumber: null,
            valueBoolean: Boolean(value),
          };
        }

        //  CASE 4: TEXT (default)
        return {
          attributeCode: code,
          optionId: null,
          valueText: value ? String(value) : null,
          valueNumber: null,
          valueBoolean: null,
        };
      });
    };
    return {
      sellerId,

      productName: form.productName,
      description: form.description,
      brand: form.brand || null,
      modelNumber: form.modelNumber || null,

      dimensionUnit: form.dimensionUnit,
      weightUnit: form.weightUnit,
      returnPolicy: form.returnPolicy,
      warrantyPeriod:
        form.warrantyPeriod !== "" ? String(form.warrantyPeriod) : null,

      currency: form.currency,

      productCondition: form.condition,

      categoryId: Number(form.categoryId),
      subCategoryId: Number(form.subCategoryId),

      tags: [],

      // PRODUCT ATTRIBUTES
      attributeRequests: mapAttributes(productAttributesValues, attributesMeta),

      // VARIANTS
      variantRequests: variants.map((v, index) => ({
        sku: v.sku,
        price: Number(v.price),
        availableQuantity:
          v.availableQuantity !== "" ? Number(v.availableQuantity) : null,

        weight: Number(v.weight || 0),
        width: Number(v.width || 0),
        height: Number(v.height || 0),
        length: Number(v.length || 0),

        isDefault: index === 0,

        attributeRequests: mapAttributes(v.attributes, attributesMeta),

        imageIds: v.imageIds || [],
      })),
    };
  };

  const handleVariantImageChange = (e, variantIndex) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = 1 * 1024 * 1024;

    const validFiles = [];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) return;
      if (file.size > maxSize) return;
      validFiles.push(file);
    }

    const previews = validFiles.map((file) => URL.createObjectURL(file));
    setVariants((prev) => {
      const copy = [...prev];

      copy[variantIndex].images = copy[variantIndex].images || [];
      copy[variantIndex].previews = copy[variantIndex].previews || [];

      copy[variantIndex].images.push(...validFiles);
      copy[variantIndex].previews.push(...previews);

      return copy;
    });
  };

  const removeVariantImage = (variantIndex, imageIndex) => {
    setVariants((prev) => {
      const copy = [...prev];

      copy[variantIndex].images.splice(imageIndex, 1);
      copy[variantIndex].previews.splice(imageIndex, 1);

      return copy;
    });
  };

  const handleVariantImageUpload = (e, variantIndex) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = 1 * 1024 * 1024; // 1MB
    const maxImagesPerVariant = 5;

    const validFiles = [];
    const previews = [];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) continue;
      if (file.size > maxSize) continue;

      validFiles.push(file);
      previews.push(URL.createObjectURL(file));
    }

    setVariants((prev) => {
      return prev.map((v, i) => {
        if (i !== variantIndex) return v;

        const existingImages = v.images || [];
        const existingPreviews = v.previews || [];

        // limit total images
        const combinedImages = [...existingImages, ...validFiles].slice(
          0,
          maxImagesPerVariant,
        );
        const combinedPreviews = [...existingPreviews, ...previews].slice(
          0,
          maxImagesPerVariant,
        );

        return {
          ...v,
          images: combinedImages,
          previews: combinedPreviews,
        };
      });
    });
  };

  /* =========================
     UI
  ========================== */
  return (
    <div className="container mt-4 mb-5">
      <div className="card shadow border-0">
        <div className="card-body p-4">
          <h1>Add Product Form</h1>
          <StepIndicator />

          {status.type && (
            <div
              className={`alert ${
                status.type === "success" ? "alert-success" : "alert-danger"
              }`}
            >
              {status.message}
            </div>
          )}

          {/* STEP 1 */}
          {step === 1 && (
            <>
              <label className="form-label">
                Category <Required />
              </label>
              <select
                className={`form-select ${
                  touched.categoryId && !form.categoryId ? "is-invalid" : ""
                }`}
                name="categoryId"
                value={form.categoryId}
                onChange={handleCategoryChange}
              >
                <option value="">Select Category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (!isStepValid(1)) return markStepTouched(1);
                  setStep(2);
                }}
              >
                Next
              </button>
            </>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <>
              <label className="form-label">
                Subcategory <Required />
              </label>
              <select
                className={`form-select ${
                  touched.subCategoryId && !form.subCategoryId
                    ? "is-invalid"
                    : ""
                }`}
                name="subCategoryId"
                value={form.subCategoryId}
                onChange={handleSubcategoryChange}
              >
                <option value="">Select Subcategory</option>
                {subCategories.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name}
                  </option>
                ))}
              </select>

              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-secondary"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>

                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (!isStepValid(2)) return markStepTouched(2);
                    setStep(3);
                  }}
                >
                  Next
                </button>
              </div>
            </>
          )}

          {/* STEP 3 */}

          {step === 3 && (
            <>
              {step3Fields.map((field) => {
                const isInvalid = touched[field.name] && !form[field.name];

                return (
                  <div key={field.name} className="mb-3">
                    <label className="form-label">
                      {field.label} {field.required && <Required />}
                    </label>

                    {field.type === "textarea" ? (
                      <textarea
                        name={field.name}
                        value={form[field.name]}
                        onChange={handleChange}
                        className={`form-control ${isInvalid ? "is-invalid" : ""}`}
                        required={field.required}
                      />
                    ) : field.type === "select" ? (
                      <select
                        name={field.name}
                        value={form[field.name]}
                        onChange={handleChange}
                        className={`form-control ${isInvalid ? "is-invalid" : ""}`}
                        required={field.required}
                      >
                        {field.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        name={field.name}
                        value={form[field.name]}
                        onChange={handleChange}
                        className={`form-control ${isInvalid ? "is-invalid" : ""}`}
                        required={field.required}
                      />
                    )}
                  </div>
                );
              })}
              <h5 className="mt-4">Product Attributes</h5>

              {attributesMeta.productAttributes.map((attr) => (
                <div key={attr.id} className="mb-3">
                  <label className="form-label">
                    {attr.name}
                    {attr.required && <Required />} {attr.placeholder}
                    {attr.unit && ` (${attr.unit})`}
                  </label>

                  {attr.inputType === "select" ? (
                    <select
                      className="form-select"
                      onChange={(e) =>
                        setProductAttributesValues((prev) => ({
                          ...prev,
                          [attr.code]: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select</option>
                      {attr.options.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.value}
                        </option>
                      ))}
                    </select>
                  ) : attr.inputType === "textarea" ? (
                    <textarea
                      className="form-control"
                      onChange={(e) =>
                        setProductAttributesValues((prev) => ({
                          ...prev,
                          [attr.code]: e.target.value,
                        }))
                      }
                    />
                  ) : attr.inputType === "checkbox" ? (
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={!!productAttributesValues[attr.code]}
                      onChange={(e) =>
                        setProductAttributesValues((prev) => ({
                          ...prev,
                          [attr.code]: e.target.checked,
                        }))
                      }
                    />
                  ) : (
                    <input
                      type={attr.inputType}
                      className="form-control"
                      onChange={(e) =>
                        setProductAttributesValues((prev) => ({
                          ...prev,
                          [attr.code]: e.target.value,
                        }))
                      }
                    />
                  )}
                </div>
              ))}

              <h5 className="mt-3">Product Images</h5>

              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="form-control mb-2"
                onChange={(e) => {
                  const files = Array.from(e.target.files);

                  setProductImages((prev) => [...prev, ...files]);

                  const previews = files.map((f) => URL.createObjectURL(f));
                  setProductPreviews((prev) => [...prev, ...previews]);
                }}
              />

              <div className="d-flex gap-2 flex-wrap mb-3">
                {productPreviews.map((src, i) => (
                  <div key={i} className="position-relative">
                    <img
                      src={src}
                      alt="preview"
                      className="rounded border"
                      style={{ width: 100, height: 100, objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0"
                      onClick={() => {
                        setProductImages((prev) =>
                          prev.filter((_, idx) => idx !== i),
                        );
                        setProductPreviews((prev) =>
                          prev.filter((_, idx) => idx !== i),
                        );
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <h5 className="mt-4">Variants</h5>

              <button
                className="btn btn-outline-primary mb-3"
                onClick={
                  () => setVariants((prev) => [...prev, createEmptyVariant()])
                  // setVariants((prev) => [
                  //   ...prev,
                  //   {
                  //     sku: "",
                  //     price: "",
                  //     attributes: {},
                  //     images: [],
                  //     previews: [],
                  //   },
                  // ])
                }
              >
                + Add Variant
              </button>

              {variants.map((variant, index) => (
                <div key={index} className="border p-3 mb-3 rounded">
                  <h6>Variant {index + 1}</h6>
                  <label className="form-label">
                    SKU
                    <Required />
                  </label>
                  <input
                    placeholder="e.g. AfroLabptop-2030"
                    className="form-control mb-2"
                    value={variant.sku}
                    onChange={(e) => {
                      const value = e.target.value;

                      setVariants((prev) =>
                        prev.map((v, i) =>
                          i === index ? { ...v, sku: value } : v,
                        ),
                      );
                    }}
                  />
                  <label className="form-label">Weight</label>
                  <input
                    type="number"
                    className="form-control mb-2"
                    value={variant.weight}
                    onChange={(e) => {
                      const copy = [...variants];
                      copy[index].weight = e.target.value;
                      setVariants(copy);
                    }}
                  />
                  <label className="form-lable">
                    Price <Required />
                  </label>
                  <input
                    placeholder="e.g 2000"
                    type="number"
                    className="form-control mb-2"
                    value={variant.price}
                    onChange={(e) => {
                      const copy = [...variants];
                      copy[index].price = e.target.value;
                      setVariants(copy);
                    }}
                  />
                  <label className="form-label">
                    Available Quantity <Required />
                  </label>
                  <input
                    className="form-control mb-2"
                    value={variant.availableQuantity || ""}
                    onChange={(e) => {
                      const copy = [...variants];
                      copy[index].availableQuantity = e.target.value;
                      setVariants(copy);
                    }}
                  />

                  {/* Variant Attributes */}
                  {attributesMeta.variantAttributes.map((attr) => (
                    <div key={attr.id} className="mb-2">
                      <label className="form-label">
                        {attr.name}
                        {attr.required && <Required />}
                        {attr.unit && ` (${attr.unit})`}
                      </label>

                      <select
                        className="form-select"
                        onChange={(e) => {
                          const copy = [...variants];
                          copy[index].attributes[attr.code] = e.target.value;
                          setVariants(copy);
                        }}
                      >
                        <option value="">-- Select --</option>
                        {attr.options.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.value}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}

                  {/* Variant Images */}
                  <label className="mt-2">Variant Images</label>

                  <input
                    type="file"
                    multiple
                    className="form-control mb-2"
                    // onChange={handleVariantImageChange}
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      const previews = files.map((f) => URL.createObjectURL(f));

                      setVariants((prev) => {
                        const copy = [...prev];
                        copy[index].images = [...copy[index].images, ...files];
                        copy[index].previews = [
                          ...(copy[index].previews || []),
                          ...previews,
                        ];
                        return copy;
                      });
                    }}
                  />

                  <div className="d-flex gap-2 flex-wrap">
                    {(variant.previews || []).map((src, i) => (
                      <div key={i} className="position-relative">
                        <img
                          src={src}
                          alt="preview"
                          style={{ width: 80, height: 80, objectFit: "cover" }}
                          className="rounded border"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-0 end-0"
                          onClick={() => {
                            setVariants((prev) => {
                              const copy = [...prev];
                              copy[index].images = copy[index].images.filter(
                                (_, idx) => idx !== i,
                              );
                              copy[index].previews = copy[
                                index
                              ].previews.filter((_, idx) => idx !== i);
                              return copy;
                            });
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Remove */}
                  <button
                    className="btn btn-sm btn-danger mt-2"
                    disabled={variants.length === 1}
                    onClick={() => {
                      setVariants((prev) => {
                        if (prev.length === 1) return prev; //  prevent deleting last
                        return prev.filter((_, i) => i !== index);
                      });
                    }}

                    // onClick={() =>
                    // setVariants((prev) => prev.filter((_, i) => i !== index))
                    // }
                  >
                    Remove
                  </button>
                </div>
              ))}

              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-secondary"
                  onClick={() => setStep(2)}
                >
                  Back
                </button>
                <button
                  className="btn btn-success"
                  disabled={submitting || !isStepValid(3)}
                  onClick={handleSubmit}
                >
                  {submitting ? (
                    <span className="d-flex align-items-center gap-2">
                      <Spinner text="Creating" /> Creating...
                    </span>
                  ) : (
                    "Create Product"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
